import React from "react";
import { convertToMoney } from "~/common/utils/currency";
import { FaWhatsapp } from "react-icons/fa";
import { event as gaEvent } from "~/lib/ga";

const DEFAULT_FINANCIAMIENTO_NUMBER = "526624984562";

const getCotizacionMessage = ({
  price,
  enganche,
  year,
  mensualidad,
  url,
  cuotas,
}) => {
  return (
    `${url}` +
    "\n\n" +
    "Cotizacion:" +
    "\n\n" +
    "Precio: " +
    convertToMoney(price) +
    "\n\n" +
    "Año: " +
    year +
    "\n\n" +
    "Mensualidades: " +
    cuotas +
    "\n\n" +
    "Enganche: " +
    convertToMoney(enganche) +
    "\n\n" +
    "Mensualidad: " +
    convertToMoney(mensualidad)
  );
};

const CoutaElement = ({ value, isActive = false, onClick, disabled }) => {
  return (
    <>
      <input
        className="sr-only peer cursor-pointer"
        type="radio"
        value={value}
        disabled={disabled}
        onChange={onClick}
        name="cuotas"
        id={`cuotas_${value}`}
      />
      <label
        htmlFor={`cuotas_${value}`}
        className={`p-4 rounded-lg cursor-pointer ${
          disabled
            ? "bg-gray-300 text-gray-100"
            : isActive
            ? "bg-brand-primary"
            : "bg-white"
        }`}
      >
        {value}
      </label>
    </>
  );
};

const GPS_PRICE = 320;
const INSURANCE_PRICE = 120;
const IVA = 1.16;
const TAZA = 0.0143; // Taza de 1.43%

export default function Cotizador({ price, year, url, contactNumber }) {
  const [enganche, setEnganche] = React.useState(0);
  const [cuota, setCuota] = React.useState("12");

  React.useEffect(() => {
    setEnganche(price * 0.35);
    setCuota("12");
  }, [price]);

  const engancheMinimo = React.useMemo(() => {
    return price * 0.35;
  }, [price]);

  const mensualidades = React.useMemo(() => {
    const capital = price - enganche;
    const mensualidad =
      (capital * (TAZA * IVA) * cuota +
        capital +
        (GPS_PRICE + INSURANCE_PRICE) * cuota) /
      cuota;
    return mensualidad;
  }, [cuota, enganche, price]);

  const handleEngancheChange = (e) => {
    const newEnganche = e.target.value;
    setEnganche(newEnganche);
  };

  const handleCoutaChange = (e) => {
    setCuota(e.target.value);
  };

  return (
    <div className="bg-gray-200 p-6 rounded-lg">
      <h2 className="text-3xl font-semibold">Cotizador</h2>
      <div className="flex w-full pt-2 gap-4">
        <div className="w-full lg:w-6/12">
          <div className="relative w-full mb-4">
            <label
              className="block uppercase text-blueGray-601 text-xs font-bold mb-2"
              htmlFor="year"
            >
              Año
            </label>
            <select
              className="border-1 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              name="year"
              id="year"
              disabled
              value={year}
            >
              <option value={year}>{year}</option>
            </select>
          </div>
        </div>
        <div className="w-full lg:w-6/12">
          <div className="relative w-full mb-3">
            <label
              className="block uppercase text-blueGray-600 text-xs font-bold mb-2"
              htmlFor="year"
            >
              Precio
            </label>
            <div className="text-xl py-1">{convertToMoney(price)}</div>
          </div>
        </div>
      </div>

      <div className="flex w-full gap-4">
        <div className="w-full lg:w-6/12">
          <div className="relative w-full mb-4">
            <label
              className="block uppercase text-blueGray-601 text-xs font-bold mb-2"
              htmlFor="enganche"
            >
              Enganche
            </label>
            <input
              type="number"
              className="border-1 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              name="enganche"
              id="enganche"
              onChange={handleEngancheChange}
              value={enganche}
            ></input>
            <p className="text-gray-700 pt-1 pl-1 text-xs italic">
              Enganche minimo:{" "}
              <span className="font-black">
                {convertToMoney(engancheMinimo)}
              </span>{" "}
              (35% sobre el precio del auto)
            </p>
          </div>
        </div>
        <div className="w-full lg:w-6/12">
          <div className="relative w-full mb-3">
            <label
              className="block uppercase text-blueGray-600 text-xs font-bold mb-2"
              htmlFor="cuotas"
            >
              Cuotas mensuales
            </label>
            {/* <div>{price}</div> */}
            <ul className="flex gap-2 pt-3">
              <li className="relative">
                <CoutaElement
                  value={12}
                  isActive={cuota === "12"}
                  onClick={handleCoutaChange}
                />
              </li>
              <li className="relative">
                <CoutaElement
                  value={24}
                  isActive={cuota === "24"}
                  onClick={handleCoutaChange}
                />
              </li>
              <li className="relative">
                <CoutaElement
                  value={36}
                  isActive={cuota === "36"}
                  onClick={handleCoutaChange}
                  disabled={year < 2013}
                />
              </li>
              <li className="relative">
                <CoutaElement
                  value={48}
                  isActive={cuota === "48"}
                  disabled={year < 2014}
                  onClick={handleCoutaChange}
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="flex flex-col w-full text-center">
        <p className="text-xl text-gray-600">Pagos Mensuales</p>
        <p className="text-3xl border-2 border-gray-300 rounded-md text-gray-900 font-black">
          {convertToMoney(mensualidades)}
        </p>
      </div>
      <div className="flex pt-3 flex-col italic text-sm w-full text-gray-500">
        <p>
          <span className="text-red-600 font-black">*</span> No incluye seguros.
        </p>
        <p>
          <span className="text-red-600 font-black">*</span> El resultado es
          aproximado, la tasa varía según modelo, año y perfil crediticio.
        </p>
      </div>
      <div className="flex justify-center pt-4">
        <button className="px-2 py-2 w-6/12 hover:bg-brand-primary bg-yellow-400 rounded-lg">
          <a
            className="flex gap-1 justify-center items-center"
            target="_blank"
            onClick={() => {
              if (contactNumber === DEFAULT_FINANCIAMIENTO_NUMBER) {
                gaEvent({
                  action: "cotizacion_whatsapp_send",
                  params: {},
                });
              } else {
                gaEvent({
                  action: "cotizacion_ext_whatsapp_send",
                  params: {
                    url,
                    contactNumber,
                  },
                });
              }
            }}
            href={`https://wa.me/${contactNumber}?text=${encodeURIComponent(
              getCotizacionMessage({
                price,
                enganche,
                year,
                cuotas: cuota,
                url,
                mensualidad: mensualidades,
              })
            )}`}
            rel="noreferrer"
          >
            <FaWhatsapp fontSize={18} />
            Enviar cotización para Financiamiento
          </a>
        </button>
      </div>
    </div>
  );
}
